import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import { getAdminCMIPayments } from "../../api/parkpass/parkpassCMIPayments";
import { getUserNames } from "../../api/parkpass/parkpassWalletTransactions";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors = {
  pending: "orange",
  completed: "green",
  failed: "red",
  cancelled: "default",
  flagged: "volcano",
};

const statusLabels = {
  pending: "En attente",
  completed: "Terminé",
  failed: "Échoué",
  cancelled: "Annulé",
  flagged: "Signalé",
};

const productTypeLabels = {
  wallet_topup: "Recharge portefeuille",
  fine: "Amende",
  ticket: "Ticket",
};

const CitizenCMIPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    statusFilter: undefined,
    productType: undefined,
    userId: undefined,
    search: "",
  });

  const fetchPayments = useCallback(
    async (
      page = pagination.current,
      pageSize = pagination.pageSize,
      currentFilters = filters,
      dates = dateRange
    ) => {
      try {
        setLoading(true);
        const response = await getAdminCMIPayments({
          page,
          pageSize,
          ...currentFilters,
          dateFrom: dates?.[0] ? dates[0].format("YYYY-MM-DD HH:mm:ss") : undefined,
          dateTo: dates?.[1] ? dates[1].format("YYYY-MM-DD HH:mm:ss") : undefined,
        });
        setPayments(response.items || []);
        setPagination({
          current: response.page || 1,
          pageSize: response.page_size || pageSize,
          total: response.total || 0,
        });
      } catch (error) {
        console.error("Error fetching CMI payments:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchPayments(1, 25, filters, dateRange);
    getUserNames()
      .then((data) => setUsers(data || []))
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchPayments(newPagination.current, newPagination.pageSize, filters, dateRange);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchPayments(1, pagination.pageSize, filters, dateRange);
  };

  const handleReset = () => {
    const resetFilters = {
      statusFilter: undefined,
      productType: undefined,
      userId: undefined,
      search: "",
    };
    setFilters(resetFilters);
    setDateRange([]);
    fetchPayments(1, pagination.pageSize, resetFilters, []);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "payment_id",
      key: "payment_id",
      width: 70,
    },
    {
      title: "Utilisateur",
      key: "user",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.user_name || "N/A"}</div>
          {record.user_email && (
            <div style={{ fontSize: 12, color: "#999" }}>{record.user_email}</div>
          )}
          <div style={{ fontSize: 11, color: "#bbb" }}>ID: {record.user_id}</div>
        </div>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {parseFloat(amount || 0).toFixed(2)} MAD
        </span>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => (
        <Tag color={statusColors[s] || "default"}>
          {statusLabels[s] || s}
        </Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "product_type",
      key: "product_type",
      width: 150,
      render: (type) => (
        <Tag color={type === "wallet_topup" ? "blue" : type === "fine" ? "red" : "cyan"}>
          {productTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: "UUID Transaction",
      dataIndex: "transaction_uuid",
      key: "transaction_uuid",
      width: 180,
      render: (uuid) => (
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>
          {uuid ? `${uuid.substring(0, 12)}...` : "N/A"}
        </span>
      ),
    },
    {
      title: "CMI Transaction ID",
      dataIndex: "cmi_transaction_id",
      key: "cmi_transaction_id",
      width: 160,
      render: (id) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id || "-"}</span>
      ),
    },
    {
      title: "Signalé",
      dataIndex: "flagged_for_review",
      key: "flagged_for_review",
      width: 90,
      render: (flagged) =>
        flagged ? <Tag color="red">Oui</Tag> : <Tag color="default">Non</Tag>,
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString("fr-FR") : "N/A"),
    },
    {
      title: "Date de paiement",
      dataIndex: "completed_at",
      key: "completed_at",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString("fr-FR") : "-"),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <CreditCardOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Paiements CMI
            </Title>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Statut"
              style={{ width: "100%" }}
              value={filters.statusFilter}
              onChange={(v) => handleFilterChange("statusFilter", v)}
              allowClear
            >
              <Option value="pending">En attente</Option>
              <Option value="completed">Terminé</Option>
              <Option value="failed">Échoué</Option>
              <Option value="cancelled">Annulé</Option>
              <Option value="flagged">Signalé</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Type"
              style={{ width: "100%" }}
              value={filters.productType}
              onChange={(v) => handleFilterChange("productType", v)}
              allowClear
            >
              <Option value="wallet_topup">Recharge</Option>
              <Option value="fine">Amende</Option>
              <Option value="ticket">Ticket</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Utilisateur"
              style={{ width: "100%" }}
              value={filters.userId}
              onChange={(v) => handleFilterChange("userId", v)}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {users.map((u) => (
                <Option key={u.user_id} value={u.user_id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <RangePicker
              style={{ width: "100%" }}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder={["Date début", "Date fin"]}
              value={dateRange.length ? dateRange : null}
              onChange={(dates) => setDateRange(dates || [])}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Input
              placeholder="Rechercher..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Rechercher
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={payments}
          rowKey="payment_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} paiements`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>
    </div>
  );
};

export default CitizenCMIPaymentsPage;
